import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, Image,
  ActivityIndicator, Dimensions,
} from 'react-native';
import { showAlert } from '../services/alert';
import { pickFromCamera, pickFromLibrary, PickedAsset } from '../services/photoPicker';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { StackScreenProps } from '@react-navigation/stack';
import {
  getProfilePhotos, uploadProfilePhoto, updateProfilePhoto,
  setMainPhoto, deleteProfilePhoto, getPhotoConfig,
} from '../services/api';
import { THEME } from '../components';
import { useLanguage } from '../context/LanguageContext';
import type { ProfilePhoto, RootStackParamList } from '../types';

type Props = StackScreenProps<RootStackParamList, 'Photo'>;

const toHttps = (url?: string) => url && !url.startsWith('http') ? `https://${url}` : url;
const { width } = Dimensions.get('window');
const SLOT_SIZE = (width - 32 - 10) / 2; // 2 columns, 16px side padding, 10px gap

const PhotoScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useLanguage();
  const { profileId } = route.params;
  const [photos, setPhotos] = useState<ProfilePhoto[]>([]);
  const [maxPhotos, setMaxPhotos] = useState(3);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [actionPhotoId, setActionPhotoId] = useState<string | null>(null);

  useEffect(() => {
    fetchPhotos();
    getPhotoConfig()
      .then(res => setMaxPhotos(res.data.maxPhotos ?? 3))
      .catch(() => {}); // keep default on failure
  }, []);

  const fetchPhotos = async () => {
    try {
      const res = await getProfilePhotos(profileId);
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch {
      Toast.show({ type: 'error', text1: 'Failed to load photos' });
    } finally {
      setLoading(false);
    }
  };

  const buildFormData = (asset: PickedAsset, forProfileId: string): FormData => {
    const formData = new FormData();
    if (asset.webFile) {
      formData.append('photo', asset.webFile, asset.name);
    } else {
      formData.append('photo', {
        uri: asset.uri,
        type: asset.type,
        name: asset.name,
      } as unknown as Blob);
    }
    formData.append('profileId', forProfileId);
    formData.append('mainPhoto', photos.length === 0 ? 'true' : 'false');
    return formData;
  };

  const openCamera = async (replacePhotoId?: string) => {
    try {
      const asset = await pickFromCamera();
      if (asset) await handlePickerResponse(asset, replacePhotoId);
    } catch (e) {
      showAlert('Permission Denied', (e as Error).message);
    }
  };

  const pickAndUpload = (replacePhotoId?: string) => {
    showAlert('Select Photo', 'Choose a source', [
      {
        text: 'Camera',
        onPress: () => openCamera(replacePhotoId),
      },
      {
        text: 'Gallery',
        onPress: async () => {
          const asset = await pickFromLibrary();
          if (asset) await handlePickerResponse(asset, replacePhotoId);
        },
      },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const handlePickerResponse = async (asset: PickedAsset, replacePhotoId?: string) => {
    setUploading(true);
    try {
      const formData = buildFormData(asset, profileId);
      if (replacePhotoId) {
        const updated = await updateProfilePhoto(replacePhotoId, formData);
        setPhotos(ps => ps.map(p => p.id === replacePhotoId ? updated.data : p));
        Toast.show({ type: 'success', text1: 'Photo replaced', text2: 'Pending admin approval' });
      } else {
        const added = await uploadProfilePhoto(formData);
        setPhotos(ps => [...ps, added.data]);
        Toast.show({ type: 'success', text1: 'Photo uploaded', text2: 'Pending admin approval' });
      }
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Upload failed', text2: (e as Error).message });
    } finally {
      setUploading(false);
    }
  };

  const handleSetMain = async (photoId: string) => {
    setActionPhotoId(photoId);
    try {
      const res = await setMainPhoto(photoId);
      setPhotos(ps => ps.map(p => ({ ...p, mainPhoto: p.id === photoId })));
      Toast.show({ type: 'success', text1: 'Main photo updated' });
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Failed to set main photo', text2: (e as Error).message });
    } finally {
      setActionPhotoId(null);
    }
  };

  const handleDelete = (photo: ProfilePhoto) => {
    showAlert('Delete Photo', 'Are you sure you want to delete this photo?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setActionPhotoId(photo.id);
          try {
            await deleteProfilePhoto(photo.id);
            setPhotos(ps => ps.filter(p => p.id !== photo.id));
            Toast.show({ type: 'success', text1: 'Photo deleted' });
          } catch (e) {
            Toast.show({ type: 'error', text1: 'Failed to delete', text2: (e as Error).message });
          } finally {
            setActionPhotoId(null);
          }
        },
      },
    ]);
  };

  const showPhotoOptions = (photo: ProfilePhoto) => {
    const options: { text: string; onPress?: () => void; style?: 'cancel' | 'destructive' }[] = [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Replace Photo', onPress: () => pickAndUpload(photo.id) },
      { text: 'Delete', style: 'destructive', onPress: () => handleDelete(photo) },
    ];
    if (photo.photoStatus === 'APPROVED' && !photo.mainPhoto) {
      options.splice(1, 0, { text: '★ Set as Main Photo', onPress: () => handleSetMain(photo.id) });
    }
    showAlert('Photo Options', undefined, options);
  };

  const renderPhotoSlot = (index: number) => {
    const photo = photos[index];
    const isActing = actionPhotoId === photo?.id;

    if (!photo) {
      // Empty slot
      if (photos.length <= index && photos.length < maxPhotos && index === photos.length) {
        return (
          <TouchableOpacity
            key={`empty-${index}`}
            style={styles.slot}
            onPress={() => !uploading && pickAndUpload()}
            disabled={uploading}>
            {uploading && index === photos.length ? (
              <ActivityIndicator color={THEME.primary} />
            ) : (
              <>
                <Icon name="add-circle-outline" size={32} color={THEME.primary} />
                <Text style={styles.addText}>{t('label.addPhoto')}</Text>
              </>
            )}
          </TouchableOpacity>
        );
      }
      // Locked empty slot
      return (
        <View key={`locked-${index}`} style={[styles.slot, styles.slotLocked]}>
          <Icon name="image-outline" size={28} color={THEME.border} />
        </View>
      );
    }

    // Photo slot
    const imageUri = toHttps(photo.photoUrl);
    return (
      <TouchableOpacity
        key={photo.id}
        style={styles.slot}
        onPress={() => showPhotoOptions(photo)}
        disabled={isActing}>
        {imageUri ? (
          <Image source={{ uri: imageUri }} style={styles.slotImage} resizeMode="cover" />
        ) : (
          <Icon name="image-outline" size={28} color={THEME.textLight} />
        )}

        {isActing && (
          <View style={styles.slotOverlay}>
            <ActivityIndicator color="#fff" />
          </View>
        )}

        {/* Status badge */}
        <View style={[
          styles.statusBadge,
          photo.photoStatus === 'APPROVED' ? styles.badgeApproved
          : photo.photoStatus === 'REJECTED' ? styles.badgeRejected
          : styles.badgePending,
        ]}>
          <Text style={styles.statusBadgeText}>
            {photo.photoStatus === 'APPROVED' ? '✓' : photo.photoStatus === 'REJECTED' ? '✗' : '⏳'}
          </Text>
        </View>

        {/* Main badge */}
        {photo.mainPhoto && (
          <View style={styles.mainBadge}>
            <Text style={styles.mainBadgeText}>★</Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>{t('screen.myPhotos')}</Text>
        <View style={styles.countBadge}>
          <Text style={styles.countText}>{photos.length} / {maxPhotos}</Text>
        </View>
      </View>

      <Text style={styles.subtitle}>{t('photo.subtitle')}</Text>

      {/* Photo Grid */}
      {loading ? (
        <ActivityIndicator size="large" color={THEME.primary} style={{ marginTop: 40 }} />
      ) : (
        <View style={styles.grid}>
          {Array.from({ length: maxPhotos }, (_, i) => i).map(i => renderPhotoSlot(i))}
        </View>
      )}

      {/* Legend */}
      <View style={styles.legend}>
        <View style={styles.legendRow}>
          <View style={[styles.legendDot, styles.badgeApproved]} />
          <Text style={styles.legendText}>{t('photo.legend.approved')}</Text>
        </View>
        <View style={styles.legendRow}>
          <View style={[styles.legendDot, styles.badgePending]} />
          <Text style={styles.legendText}>{t('photo.legend.pending')}</Text>
        </View>
        <View style={styles.legendRow}>
          <View style={[styles.legendDot, styles.badgeRejected]} />
          <Text style={styles.legendText}>{t('photo.legend.rejected')}</Text>
        </View>
        <View style={styles.legendRow}>
          <Text style={styles.legendStar}>★</Text>
          <Text style={styles.legendText}>{t('photo.legend.main')}</Text>
        </View>
      </View>

      {/* Guidelines */}
      <View style={styles.tips}>
        <Text style={styles.tipTitle}>{t('photo.tips.title')}</Text>
        <Text style={styles.tip}>{t('photo.tips.clearFace')}</Text>
        <Text style={styles.tip}>{t('photo.tips.recent')}</Text>
        <Text style={styles.tip}>{t('photo.tips.solo')}</Text>
        <Text style={styles.tip}>{`${t('photo.tips.maxSize')} ${maxPhotos}`}</Text>
      </View>
    </ScrollView>
  );
};

export default PhotoScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: THEME.bg },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingTop: 20, paddingBottom: 8,
  },
  title: { fontSize: 22, fontWeight: '900', color: THEME.text },
  countBadge: {
    backgroundColor: THEME.primary, paddingHorizontal: 12, paddingVertical: 4,
    borderRadius: 12,
  },
  countText: { color: '#fff', fontWeight: '800', fontSize: 13 },
  subtitle: { fontSize: 13, color: THEME.textLight, paddingHorizontal: 16, marginBottom: 20, lineHeight: 19 },
  grid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 10,
    paddingHorizontal: 16, marginBottom: 24,
  },
  slot: {
    width: SLOT_SIZE, height: SLOT_SIZE * 1.25, borderRadius: 16,
    backgroundColor: THEME.card, borderWidth: 1.5, borderColor: THEME.border,
    alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  },
  slotLocked: { opacity: 0.4 },
  slotImage: { width: '100%', height: '100%' },
  slotOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center',
  },
  addText: { fontSize: 11, color: THEME.primary, fontWeight: '700', marginTop: 4 },
  statusBadge: {
    position: 'absolute', top: 5, right: 5,
    width: 20, height: 20, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
  },
  badgeApproved: { backgroundColor: '#27AE60' },
  badgePending: { backgroundColor: '#F39C12' },
  badgeRejected: { backgroundColor: '#E74C3C' },
  statusBadgeText: { color: '#fff', fontSize: 10, fontWeight: '800' },
  mainBadge: {
    position: 'absolute', bottom: 5, left: 5,
    backgroundColor: THEME.primary, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6,
  },
  mainBadgeText: { color: '#fff', fontSize: 11, fontWeight: '800' },
  legend: {
    marginHorizontal: 16, backgroundColor: THEME.card, borderRadius: 12,
    padding: 14, marginBottom: 16, gap: 8,
  },
  legendRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  legendDot: { width: 14, height: 14, borderRadius: 7 },
  legendStar: { fontSize: 14, color: THEME.primary, width: 14, textAlign: 'center' },
  legendText: { fontSize: 13, color: THEME.textLight },
  tips: {
    marginHorizontal: 16, backgroundColor: THEME.card, borderRadius: 12,
    padding: 16, borderWidth: 1, borderColor: THEME.border,
  },
  tipTitle: { fontSize: 14, fontWeight: '700', color: THEME.text, marginBottom: 10 },
  tip: { fontSize: 13, color: THEME.textLight, lineHeight: 24 },
});
